import { useMemo, type FC } from 'react';

import { SideNavigation } from '@livechat/design-system-react-components';
import { useSelector } from 'react-redux';

import { plural } from 'helpers/string';
import { API_ACCESS_ROUTE, isApiAccessEnabled } from 'routes/engage/api-access/constants';
import { useCanManageApi } from 'routes/engage/api-access/hooks/use-can-manage-api';
import { EngageSideNavigationItem } from 'routes/engage/side-navigation/EngageSideNavigationItem';
import { getActiveGreetingsCount } from 'store/entities/greetings/selectors';
import { getCanManageGreetings } from 'store/features/session/selectors';

import { useActiveGoalsCount } from '../hooks/use-active-goals-count';
import { useCustomersCount } from '../hooks/use-customers-count';
import { useEngageApplications } from '../hooks/use-engage-applications';

const EngageSideNavigation: FC = () => {
  const canManageGreetings = useSelector(getCanManageGreetings);
  const { canManageApi } = useCanManageApi();
  const activeGreetingsCount = useSelector(getActiveGreetingsCount);
  const activeGoalsCount = useActiveGoalsCount();
  const { applications } = useEngageApplications();
  const { customersCount, isLoading } = useCustomersCount();

  const items = useMemo(
    () =>
      [
        {
          title: 'Traffic',
          path: '/engage/traffic',
          label: (count: number) => plural(count, 'customer', 'customers'),
          badge: {
            count: customersCount,
            max: 998,
            loading: isLoading,
          },
          enabled: true,
        },
        {
          title: 'Campaigns',
          path: '/engage/campaigns',
          label: () => 'active',
          badge: {
            count: activeGreetingsCount,
          },
          enabled: canManageGreetings,
        },
        {
          title: 'Goals',
          path: '/engage/goals',
          label: () => 'active',
          badge: {
            count: activeGoalsCount,
          },
          enabled: canManageGreetings,
        },
        {
          title: 'API access',
          path: API_ACCESS_ROUTE,
          label: null,
          badge: null,
          enabled: canManageApi && isApiAccessEnabled(),
        },
        ...applications.map(({ name, id }) => ({
          title: name,
          path: `/engage/applications/${id}`,
          label: null,
          badge: null,
          enabled: true,
        })),
      ].filter((item) => item.enabled),
    [applications, customersCount, isLoading, activeGreetingsCount, activeGoalsCount, canManageGreetings, canManageApi],
  );

  return (
    <SideNavigation data-testid="side-navigation" title="Engage" noGaps>
      {items.map(({ title, path, label, badge }) => (
        <EngageSideNavigationItem
          path={path}
          title={title}
          key={path}
          label={label ? label : undefined}
          badgeCount={(badge as { count: number })?.count}
          badgeMax={(badge as { max: number })?.max}
        />
      ))}
    </SideNavigation>
  );
};

export default EngageSideNavigation;
